import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ShareIcon from "../../assets/icons/share.svg";
import CopyIcon from "../../assets/icons/copy.svg";
import MapAddressDetails from "../Maps/MapAddressDetails";
import Footer from "../Footer/Footer";
import Header from "../Header/Header";
import { getUserProfile, UserProfile } from "../../API/UserProfileApi/GetUserProfile";
import { Address } from "../../API/Address/ViewAllAddedAddressByUserAsync";
import OfficeIcon from "../../assets/icons/OfficeList.svg";
import HomeIcon from "../../assets/icons/HomeLIst.svg";
import OtherIcon from "../../assets/icons/OtherList.svg";
import FavouriteTrue from "../../assets/icons/favouritetrue.svg";
import FavouriteFalse from "../../assets/icons/favouritefalse.svg";
import LocationIcon from "../../assets/icons/Newmap.svg";

import {
  EmailShareButton,
  WhatsappShareButton,
  FacebookShareButton,
  TwitterShareButton,
} from "react-share";
import { EmailIcon, WhatsappIcon, FacebookIcon, TwitterIcon } from "react-share";
import CheckIcon from "../../assets/icons/checked.svg";
import { updateLocalStorageSearchHistory } from "../../utils/updateLocalStorageSearchHistory";
import { RemoveFromFavouritesAsync } from "../../API/AddressFavourites/RemoveFromFavouritesAsync";
import { AddtoFavouritesAsync } from "../../API/AddressFavourites/AddToFavouritesAsync";
import { getUserIdFromToken } from "../../auth/authUtils";
import { ShareLink } from "../../API/Address/SharePost";
import { useLocalization } from "../Context/MultiLanguageContext";

interface SearchHistoryItem {
  query: string;
  results: any;
  outsideBlock?: boolean;
}

const FavouriteAddressDetails: React.FC<{ updateUserName: (newUserName: string) => void }> = ({
  updateUserName,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLocalization();

  const location = useLocation();
  const stateAddress = (location.state as { address?: Address })?.address;

  const [addressResults, setAddressResults] = useState<Address[]>([]);
  const [recentSearchHistory, setRecentSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [userName, setUserName] = useState("");
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const initialAddress = stateAddress ? { ...stateAddress, isFavourite: true } : null;
  const [address, setAddress] = useState<Address | null>(initialAddress);
  const [shareUrls, setShareUrls] = useState<{ [key: string]: { url: string; referralLink?: string } }>({});
  const [isLoadingShare, setIsLoadingShare] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    const storedUserName = localStorage.getItem("userName");
    if (storedUserName) {
      setUserName(storedUserName);
    }
  }, []);

  const handleLocationIconClick = (latitude: number, longitude: number) => {
    const defaultMap = localStorage.getItem("defaultMap") || "google";

    if (defaultMap === "waze") {
      const wazeMapsUrl = `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`;
      window.open(wazeMapsUrl, "_blank");
    } else if (defaultMap === "mapindia") {
      const mapplsUrl = `https://www.mappls.com/@${latitude},${longitude}`;
      window.open(mapplsUrl, "_blank");
    } else if (defaultMap === "apple") {
      const appleMapsUrl = `https://maps.apple.com/?ll=${latitude},${longitude}&q=${latitude},${longitude}&z=18&t=m`;
      window.open(appleMapsUrl, "_blank");
    } else {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      window.open(googleMapsUrl, "_blank");
    }
  };

  const HeartClick = async (addressId: number, isFavourite: boolean) => {
    setAddress((prev) =>
      prev && Number(prev.id) === addressId ? { ...prev, isFavourite: !prev.isFavourite } : prev
    );

    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      if (!isFavourite) {
        await AddtoFavouritesAsync(addressId, token);
        updateLocalStorageSearchHistory(addressId, true);
      } else {
        await RemoveFromFavouritesAsync(addressId, token);
        updateLocalStorageSearchHistory(addressId, false);
      }
    } catch (error) { }
  };

  const fetchAndHandleUserProfile = async () => {
    try {
      const data = await getUserProfile(t);
      setUserProfile(data);
      setUserName(data.userName);
      updateUserName(data.userName);
      navigate("/profile", {
        state: {
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
        },
      });
    } catch (error) {
      toast.error(t("sessionExpired"));
      navigate("/home");
    }
  };

  useEffect(() => {
    if (!address) {
      setErrorMessage(t("addressdataNotAvailable"));
    }
  }, [address, t]);

  function formatPamCode(address: any): string {
    const pin = String(address?.pinCode ?? "").trim();
    const block = String(address?.blockCode ?? "").trim();
    const addr = String(address?.addressCode ?? "").trim();

    if (!pin || !block || !addr) return "N/A";

    return [pin, block, addr].join("-");
  }

  const handleShareClick = async () => {
    if (showShareOptions) {
      setShowShareOptions(false);
      return;
    }

    if (!address?.id) {
      console.error("No address ID available for sharing");
      return;
    }

    setIsLoadingShare((prev) => ({ ...prev, [address.id]: true }));

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error(t("sessionExpired"));
        navigate("/");
        return;
      }

      const shareResponse = await ShareLink(parseInt(address.id), token);

      if (shareResponse && shareResponse.url) {
        setShareUrls((prev) => ({
          ...prev,
          [address.id]: {
            url: shareResponse.url,
            referralLink: shareResponse.referralLink
          }
        }));
        setShowShareOptions(true);
      } else {
        toast.error(t("errorGeneratingShareLink"));
      }
    } catch (error) {
      console.error("Error calling share API:", error);
      toast.error(t("errorGeneratingShareLink"));
    } finally {
      setIsLoadingShare((prev) => ({ ...prev, [address.id]: false }));
    }
  };

  const handleCopy = async (textToCopy: string): Promise<void> => {
    try {
      const finalText = String(textToCopy ?? "").trim();
      await navigator.clipboard.writeText(finalText);
      setCopiedValue(finalText);
      setTimeout(() => setCopiedValue(null), 2000);
    } catch (err) { }
  };

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("authToken");
    localStorage.removeItem("token");
    localStorage.removeItem("userName");

    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const shareMessage = (address: Address) => {
    const shareData = shareUrls[address.id];
    const dynamicUrl = shareData?.url;
    const referralLink = shareData?.referralLink;
    const fallbackUrl = `https://app.getmethere.in/${formatPamCode(address)}`;
    const addressLink = dynamicUrl || fallbackUrl;

    const isPreprod = process.env.REACT_APP_API_BASE_URL === "https://preprod-api.getmethere.in";
    const firstName = (userName || "").trim().split(/\s+/)[0] || "";
    const userId = getUserIdFromToken();
    const userRefLink = referralLink
      ? referralLink
      : (isPreprod
        ? `https://preprod.getmethere.in/?ref=${userId || ""}&by=${firstName}`
        : `https://getmethere.in/?ref=${userId || ""}&by=${firstName}`);

    return `Check out this address:
${address.addressLine1}, ${address.addressLine2}, ${address.addressLine3}
PAM Code: ${formatPamCode(address)}
Digi Pin: ${address?.digiPin || "N/A"}
Address Link: ${addressLink}

More details: ${userRefLink}`;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <ToastContainer />
      <Header
        userName={userName}
        fetchUserProfile={fetchAndHandleUserProfile}
        handleLogout={handleLogout}
      />

      <main className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-md p-4 md:p-6">
          <div className="relative h-[250px] sm:h-[300px] md:h-[350px]">
            {address ? (
              <MapAddressDetails addresses={[address]} />
            ) : (
              <p className="text-center text-gray-500">{t("loadingMap")}</p>
            )}
          </div>

          {successMessage && (
            <p className="mt-4 text-center text-green-500">{successMessage}</p>
          )}

          {address ? (
            <div className="mt-2">
              <div className="p-4 bg-white rounded-lg relative space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex gap-2 text-lg font-bold items-center">
                    <img
                      src={
                        address.addressType.toLowerCase() === "office"
                          ? OfficeIcon
                          : address.addressType.toLowerCase() === "home"
                            ? HomeIcon
                            : OtherIcon
                      }
                      alt="Type Icon"
                      className="w-8 h-8 md:w-10 md:h-10"
                    />
                    <span>
                      {["office", "home", "other"].includes(address.addressType.toLowerCase())
                        ? t(address.addressType.toLowerCase())
                        : address.addressType}
                    </span>
                  </div>
                </div>

                <div className="space-y-1">
                  <p>{t("buildingNumber")} : {address.addressLine1}</p>
                  <p>{t("street")} : {address.addressLine2}</p>
                  <p>{t("city")} : {address.addressLine3}</p>
                  {/* <p>{t("postalCode")} : {address.pinCode}</p> */}
                  <p>{t("postOffice")} : {address.postOfficeName}</p>
                </div>

                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mt-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4 w-full xl:w-auto">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
                      <label
                        htmlFor="pamCode"
                        className="text-sm md:text-base font-medium whitespace-nowrap w-24"
                      >
                        {t("pamCode")}:
                      </label>

                      <div className="relative flex-1 sm:w-auto md:w-[180px] lg:w-[200px]">
                        <input
                          id="pamCode"
                          type="text"
                          value={formatPamCode(address)}
                          className="text-sm md:text-base lg:text-lg p-2 pr-10 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                          readOnly
                        />
                        <button
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-500 transition-colors"
                          aria-label="Copy PAM Code"
                          onClick={() => {
                            const copiedPam = [
                              String(address?.pinCode ?? "").trim(),
                              String(address?.blockCode ?? "").trim(),
                              String(address?.addressCode ?? "").trim(),
                            ].join("-");
                            handleCopy(copiedPam);
                          }}
                        >
                          <img
                            src={
                              copiedValue ===
                                [
                                  String(address?.pinCode ?? "").trim(),
                                  String(address?.blockCode ?? "").trim(),
                                  String(address?.addressCode ?? "").trim(),
                                ].join("-")
                                ? CheckIcon
                                : CopyIcon
                            }
                            alt="Copy"
                            className="w-5 h-5 md:w-6 md:h-6"
                          />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
                      <label
                        htmlFor="digipin"
                        className="text-sm md:text-base font-medium whitespace-nowrap w-24"
                      >
                        {t("digiPin")}:
                      </label>

                      <div className="relative flex-1 sm:w-auto md:w-[180px] lg:w-[200px]">
                        <input
                          id="digipin"
                          type="text"
                          value={address.digiPin || ""}
                          className="text-sm md:text-base p-2 pr-10 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                          readOnly
                        />
                        <button
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-500 transition-colors"
                          aria-label="Copy Digi Pin"
                          onClick={() => handleCopy(address.digiPin || "")}
                        >
                          <img
                            src={copiedValue === (address.digiPin || "") ? CheckIcon : CopyIcon}
                            alt="Copy"
                            className="w-5 h-5 md:w-6 md:h-6"
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 justify-center sm:justify-start xl:justify-end items-center mt-2 xl:mt-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        HeartClick(Number(address.id), address.isFavourite);
                      }}
                      title="Remove from Favourites"
                      className="w-12 h-10 sm:w-16 sm:h-12 md:w-20 md:h-12 lg:w-24 lg:h-12 flex items-center justify-center p-1 border border-gray-200 bg-white rounded-md hover:bg-blue-100 focus:outline-none transition-colors"
                    >
                      <img
                        src={address.isFavourite ? FavouriteTrue : FavouriteFalse}
                        alt="Favourites"
                        className="w-5 h-5 sm:w-6 sm:h-6"
                      />
                    </button>

                    <button
                      onClick={handleShareClick}
                      disabled={isLoadingShare[address?.id]}
                      className={`w-12 h-10 sm:w-16 sm:h-12 md:w-20 md:h-12 lg:w-24 lg:h-12 flex items-center justify-center p-1 border border-gray-200 bg-white rounded-md hover:bg-blue-100 focus:outline-none transition-colors ${isLoadingShare[address?.id] ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                    >
                      {isLoadingShare[address?.id] ? (
                        <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <img src={ShareIcon} alt="Share" className="w-5 h-5 sm:w-6 sm:h-6" />
                      )}
                    </button>

                    <button
                      title="Location"
                      className="w-12 h-10 sm:w-16 sm:h-12 md:w-20 md:h-12 lg:w-24 lg:h-12 flex items-center justify-center p-1 border border-gray-200 bg-white rounded-md hover:bg-blue-100 focus:outline-none transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLocationIconClick(address.latitude, address.longitude);
                      }}
                    >
                      <img
                        src={LocationIcon}
                        alt="Location"
                        className="w-5 h-5 sm:w-6 sm:h-6"
                      />
                    </button>
                  </div>
                </div>
              </div>

              {showShareOptions && (
                <div className="flex gap-4 justify-end mr-20">
                  <EmailShareButton
                    url={shareMessage(address)}
                    className="transform transition-transform duration-300 hover:scale-150 hover:shadow-lg"
                  >
                    <EmailIcon
                      size={32}
                      round
                      style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", borderRadius: "50%" }}
                    />
                  </EmailShareButton>

                  <WhatsappShareButton
                    url={shareMessage(address)}
                    className="transform transition-transform duration-300 hover:scale-150 hover:shadow-lg"
                  >
                    <WhatsappIcon
                      size={32}
                      round
                      style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", borderRadius: "50%" }}
                    />
                  </WhatsappShareButton>

                  <FacebookShareButton
                    url={shareMessage(address)}
                    className="transform transition-transform duration-300 hover:scale-150 hover:shadow-lg"
                  >
                    <FacebookIcon
                      size={32}
                      round
                      style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", borderRadius: "50%" }}
                    />
                  </FacebookShareButton>

                  <TwitterShareButton
                    url={shareMessage(address)}
                    className="transform transition-transform duration-300 hover:scale-150 hover:shadow-lg"
                  >
                    <TwitterIcon
                      size={32}
                      round
                      style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", borderRadius: "50%" }}
                    />
                  </TwitterShareButton>
                </div>
              )}
            </div>
          ) : (
            <p className="mt-6 text-center text-gray-500">{t("loadingAddressDetails")}</p>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FavouriteAddressDetails;